// BOM Upload Page: one import page for tabular, CAD assembly and customer-package bills
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  TextField,
  Stack,
  alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as TableIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { bomIntelligenceService } from '../../services/bomIntelligenceService';
import { semanticColors } from '../../theme/theme';
import { useAppDispatch, setBreadcrumbItems } from '@ciea.ai/smart-infra-navigation';

// ── Import modes — user picks one card, everything else (workflow, column mapping,
// part pool, accept list) is derived from the mode + file. No further knobs needed. ──
type ImportMode = 'standard' | 'package';

const IMPORT_MODES: {
  id: ImportMode;
  label: string;
  desc: string;
  icon: React.ReactNode;
  formats: string;
  accept: string;
}[] = [
  {
    id: 'standard',
    label: 'Standard BOM',
    desc: 'Tabular data or CAD assembly with part numbers and hierarchy.',
    icon: <TableIcon sx={{ fontSize: 24 }} />,
    formats: 'CSV, XLSX, JSON, STEP, IGES',
    accept: '.csv,.xlsx,.xls,.json,.step,.stp,.iges,.igs',
  },
  {
    id: 'package',
    label: 'Customer Package (ZIP)',
    desc: 'Build-to-print package: PLM/ERP BOM exports + drawings + process specs + CAD, parsed via a customer profile.',
    icon: <UploadIcon sx={{ fontSize: 24 }} />,
    formats: 'ZIP',
    accept: '.zip',
  },
];

// Workflow auto-selected by mode (sent to backend; not user-pickable).
const MODE_DEFAULT_WORKFLOW: Record<ImportMode, string> = {
  standard: 'spec-upload',
  package: 'spec-upload',
};

const CAD_EXTENSIONS = ['.step', '.stp', '.iges', '.igs'];
const MAX_FILE_SIZE = 200 * 1024 * 1024;

type UploadState = 'idle' | 'dragging' | 'uploading' | 'processing' | 'success' | 'error';

const BomUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  React.useEffect(() => {
    dispatch(setBreadcrumbItems([
      { label: 'Bill of Materials', path: '/bom' },
      { label: 'Import', path: '/bom-intelligence/upload' },
    ]));
  }, [dispatch]);

  // Import mode (user-picked card) — drives accept list + workflowId
  const [importMode, setImportMode] = useState<ImportMode>('standard');

  // Customer-package mode: which ingestion profile parses the package
  // (column maps, folder convention, status vocabulary). Profiles are
  // seeded from committed JSON — 'camtek' is the first.
  const [profileKey, setProfileKey] = useState('');
  // Profiles come from the server: the key must match a committed profile
  // exactly, so typing it by hand was a spelling test whose failure mode was a
  // silent 400 after the whole ZIP had already uploaded.
  const [profiles, setProfiles] = useState<Array<{ profileKey: string; name: string }>>([]);
  useEffect(() => {
    if (importMode !== 'package') return;
    bomIntelligenceService
      .listIngestionProfiles()
      .then((list) => {
        setProfiles(list);
        // Generic reads any package; a customer profile is picked when the package is theirs.
        setProfileKey((cur) => cur || list.find((p) => p.profileKey === 'generic')?.profileKey || list[0]?.profileKey || '');
      })
      .catch(() => setProfiles([]));
  }, [importMode]);

  // Single-file state (tabular + CAD)
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bomName, setBomName] = useState('');
  const [description, setDescription] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploadBytes, setUploadBytes] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadStartRef = useRef<number>(0);
  const uploadAbortRef = useRef<AbortController | null>(null);

  // CAD detection from extension — only relevant in `standard` mode.
  const isCadFile = !!selectedFile
    && CAD_EXTENSIONS.includes('.' + (selectedFile.name.split('.').pop()?.toLowerCase() || ''));

  // Detected flow label — derived from the picked mode + the specific file
  // (so "Standard" shows "CAD Assembly" when a CAD file is picked).
  const detectedFlow = (() => {
    if (importMode === 'package') {
      return { label: 'Customer Package', description: 'ZIP walked + content-hash deduped; BOM spreadsheets parsed via the profile; drawings/process specs/CAD registered and linked per line.' };
    }
    if (isCadFile) {
      return { label: 'CAD Assembly', description: 'Hierarchy, materials and geometry extracted automatically; dedicated CAD enrichment workflow runs post-import.' };
    }
    if (selectedFile) {
      return { label: 'Standard Tabular BOM', description: 'Column mapping detected on the preview step.' };
    }
    return null;
  })();

  // ── Helpers ──
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const resetState = useCallback(() => {
    setUploadState('idle');
    setSelectedFile(null);
    setBomName('');
    setDescription('');
    setProgress(0);
    setUploadBytes(null);
    setError(null);
  }, []);

  // ── Validate the picked file against the mode's accept list ──
  const validateSingleFile = useCallback((file: File, mode: ImportMode): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const modeConfig = IMPORT_MODES.find(m => m.id === mode);
    const accepted = (modeConfig?.accept || '').split(',');
    if (!accepted.includes(ext)) return `Unsupported format for ${modeConfig?.label}: ${ext}. Accepted: ${modeConfig?.formats}`;
    if (file.size > MAX_FILE_SIZE) return `File size exceeds 200MB limit`;
    return null;
  }, []);

  // ── Single file handling (tabular + CAD) ──
  const handleFile = useCallback((file: File) => {
    const validationError = validateSingleFile(file, importMode);
    if (validationError) {
      setError(validationError);
      setUploadState('error');
      return;
    }
    setSelectedFile(file);
    setError(null);
    setUploadState('idle');
    if (!bomName) {
      setBomName(file.name.replace(/\.[^/.]+$/, ''));
    }
  }, [validateSingleFile, importMode, bomName]);

  // ── Drag & drop ──
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('dragging');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('idle');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadState('idle');

    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    handleFile(files[0]);
  }, [handleFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    handleFile(files[0]);
    e.target.value = '';
  }, [handleFile]);

  // ── Upload: single file (tabular/CAD) ──
  const handleUploadSingle = async () => {
    if (!selectedFile) return;

    setUploadState('uploading');
    setProgress(0);
    setUploadBytes(null);
    setError(null);
    uploadStartRef.current = Date.now();
    const abort = new AbortController();
    uploadAbortRef.current = abort;

    try {
      const result =
        importMode === 'package'
          ? await bomIntelligenceService.uploadPackage(selectedFile, {
              profileKey,
              bomName: bomName || selectedFile.name,
              bomType: 'ENGINEERING',
              description,
              signal: abort.signal,
              onUploadProgress: ({ loaded, total, percent }) => {
                setUploadBytes({ loaded, total });
                setProgress(percent);
              },
            })
          : await bomIntelligenceService.uploadFile(selectedFile, {
              bomName: bomName || selectedFile.name,
              bomType: 'ENGINEERING',
              description,
              importMode,
              signal: abort.signal,
              onUploadProgress: ({ loaded, total, percent }) => {
                setUploadBytes({ loaded, total });
                setProgress(percent);
              },
            });

      setUploadState('processing');
      setUploadBytes(null);
      setProgress(30);
      await pollAnalysisStatus(result.analysisId);
    } catch (err: any) {
      if (abort.signal.aborted) {
        setUploadState('idle');
        setProgress(0);
        setUploadBytes(null);
        return;
      }
      const msg = err.message?.includes('timeout')
        ? `Upload timed out. Please try again.`
        : err.response?.data?.message || err.message || 'Upload failed';
      setError(msg);
      setUploadState('error');
      setProgress(0);
      setUploadBytes(null);
    }
  };

  const pollAnalysisStatus = async (id: string) => {
    // Reading a package (AI + every drawing) takes minutes; files take seconds.
    const maxAttempts = importMode === 'package' ? 900 : 60;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setError('Still processing. It continues in the background; the Action Center links to it when ready.');
        setUploadState('error');
        return;
      }

      try {
        const status = await bomIntelligenceService.getAnalysisStatus(id);
        setProgress(status.progress || 30);

        if (status.status === 'PREVIEWING' || status.status === 'COMPLETE') {
          setUploadState('success');
          setProgress(100);
          const wf = encodeURIComponent(MODE_DEFAULT_WORKFLOW[importMode]);
          setTimeout(() => navigate(`/bom-intelligence/preview/${id}?workflowId=${wf}`), 1500);
          return;
        }

        if (status.status === 'FAILED') {
          setError(status.errorMessage || 'Processing failed');
          setUploadState('error');
          return;
        }

        attempts++;
        setTimeout(poll, 1000);
      } catch {
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
  };

  const handleUpload = handleUploadSingle;
  const hasFiles = !!selectedFile;
  const canUpload = hasFiles && !!bomName.trim();

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', py: 4 }}>
      {/* Header — back-nav handled by the global breadcrumb in AppLayout. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} sx={{ color: semanticColors.text.primary, mb: 0.5 }}>
          Import BOM
        </Typography>
        <Typography variant="body2" sx={{ color: semanticColors.text.tertiary }}>
          Choose an import method, then upload your files. AI will parse, enrich, and match parts automatically.
        </Typography>
      </Box>

      {/* ── Import Mode Picker — picks the whole flow (workflow, accept list, column mapping) ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(2, 1fr)' },
          gap: 1.5,
          mb: 3,
        }}
      >
        {IMPORT_MODES.map(mode => {
          const selected = importMode === mode.id;
          const disabled = uploadState !== 'idle' && uploadState !== 'error';
          return (
            <Paper
              key={mode.id}
              elevation={0}
              onClick={() => {
                if (disabled) return;
                if (importMode !== mode.id) {
                  setImportMode(mode.id);
                  resetState();
                }
              }}
              sx={{
                p: 2,
                cursor: disabled ? 'default' : 'pointer',
                border: '1.5px solid',
                borderColor: selected ? semanticColors.text.primary : semanticColors.border.light,
                borderRadius: 2,
                bgcolor: selected ? alpha(semanticColors.text.primary, 0.04) : semanticColors.bg.surface,
                transition: 'all 0.15s ease',
                '&:hover': !disabled ? {
                  borderColor: selected ? semanticColors.text.primary : semanticColors.border.emphasis,
                  bgcolor: selected ? alpha(semanticColors.text.primary, 0.04) : semanticColors.bg.subtle,
                } : {},
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: selected ? semanticColors.text.primary : alpha(semanticColors.text.primary, 0.06),
                    color: selected ? semanticColors.text.inverse : semanticColors.text.tertiary,
                  }}
                >
                  {mode.icon}
                </Box>
                {selected && (
                  <Chip
                    label="Selected"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      bgcolor: semanticColors.text.primary,
                      color: semanticColors.text.inverse,
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected ? semanticColors.text.primary : semanticColors.text.secondary,
                  mb: 0.5,
                }}
              >
                {mode.label}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: semanticColors.text.tertiary, lineHeight: 1.45, mb: 1 }}>
                {mode.desc}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: semanticColors.text.disabled, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {mode.formats}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* ── Drop Zone ── */}
      <Paper
        elevation={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (uploadState !== 'idle' && uploadState !== 'error') return;
          fileInputRef.current?.click();
        }}
        sx={{
          p: 5,
          mb: 3,
          border: '2px dashed',
          borderColor: uploadState === 'dragging'
            ? semanticColors.text.primary
            : uploadState === 'error'
            ? semanticColors.status.error
            : uploadState === 'success'
            ? semanticColors.status.success
            : hasFiles
            ? semanticColors.border.emphasis
            : semanticColors.border.default,
          borderRadius: 2,
          bgcolor: uploadState === 'dragging'
            ? alpha(semanticColors.text.primary, 0.04)
            : hasFiles
            ? alpha(semanticColors.text.primary, 0.015)
            : semanticColors.bg.subtle,
          cursor: (uploadState === 'idle' || uploadState === 'error') ? 'pointer' : 'default',
          textAlign: 'center',
          transition: 'all 0.15s ease',
          '&:hover': (uploadState === 'idle' || uploadState === 'error') ? {
            borderColor: semanticColors.text.primary,
            bgcolor: alpha(semanticColors.text.primary, 0.03),
          } : {},
        }}
      >
        {/* Tabular/CAD single-file input — accept depends on current mode */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept={IMPORT_MODES.find(m => m.id === importMode)?.accept || ''}
          onChange={handleFileSelect}
        />

        <AnimatePresence mode="wait">
          {uploadState === 'uploading' || uploadState === 'processing' ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box sx={{ px: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: semanticColors.text.primary }}>
                  {uploadState === 'uploading'
                    ? 'Uploading…'
                    : 'Processing…'}
                </Typography>
                <LinearProgress
                  variant={uploadState === 'processing' && progress < 40 ? 'indeterminate' : 'determinate'}
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: alpha(semanticColors.text.primary, 0.08),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: semanticColors.text.primary,
                    },
                  }}
                />
                {uploadState === 'uploading' && uploadBytes ? (
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: semanticColors.text.tertiary }}>
                      {formatFileSize(uploadBytes.loaded)} / {formatFileSize(uploadBytes.total)} ({progress}%)
                    </Typography>
                    <Typography variant="caption" sx={{ color: semanticColors.text.disabled }}>
                      {(() => {
                        const elapsed = (Date.now() - uploadStartRef.current) / 1000;
                        if (elapsed < 1 || uploadBytes.loaded < 1024) return '';
                        const speed = uploadBytes.loaded / elapsed;
                        const remaining = (uploadBytes.total - uploadBytes.loaded) / speed;
                        return `${formatFileSize(speed)}/s${remaining > 1 ? ` · ${Math.ceil(remaining)}s left` : ''}`;
                      })()}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ mt: 1, color: semanticColors.text.tertiary, display: 'block' }}>
                    {uploadState === 'processing'
                      ? importMode === 'package' ? 'Reading package...' : 'Parsing file...'
                      : `${progress}%`}
                  </Typography>
                )}
                {uploadState === 'uploading' && (
                  <Button
                    size="small"
                    onClick={() => uploadAbortRef.current?.abort()}
                    sx={{ mt: 1.5, textTransform: 'none', color: semanticColors.text.secondary }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </motion.div>
          ) : uploadState === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <SuccessIcon sx={{ fontSize: 44, color: semanticColors.status.success, mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: semanticColors.status.success }}>
                File processed successfully
              </Typography>
              <Typography variant="caption" sx={{ color: semanticColors.text.tertiary }}>
                Redirecting...
              </Typography>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Single file selected (tabular/CAD) */}
              {selectedFile ? (
                <Box>
                  <FileIcon sx={{ fontSize: 40, color: semanticColors.text.tertiary, mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: semanticColors.text.primary }}>
                    {selectedFile.name}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                    <Chip
                      label={selectedFile.name.split('.').pop()?.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: alpha(semanticColors.text.primary, 0.08),
                        color: semanticColors.text.primary,
                        fontWeight: 600,
                        height: 24,
                      }}
                    />
                    <Chip
                      label={formatFileSize(selectedFile.size)}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: semanticColors.border.default, height: 24 }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1.5,
                      display: 'inline-block',
                      color: semanticColors.text.tertiary,
                      cursor: 'pointer',
                      '&:hover': { color: semanticColors.text.secondary, textDecoration: 'underline' },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setBomName('');
                    }}
                  >
                    Choose a different file
                  </Typography>
                </Box>
              ) : (
                /* Empty state */
                <Box>
                  <UploadIcon sx={{ fontSize: 40, color: semanticColors.text.disabled, mb: 1 }} />
                  <Typography variant="subtitle1" fontWeight={600} sx={{ color: semanticColors.text.primary }}>
                    {uploadState === 'dragging' ? 'Drop your file(s) here' : 'Drag & drop, or click to browse'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: semanticColors.text.disabled, mt: 0.5, display: 'block' }}>
                    Tabular or CAD; the flow is inferred from the file type.
                  </Typography>
                </Box>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Paper>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{ mb: 3, borderRadius: 1.5 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* ── BOM Details Form ── */}
      {hasFiles && (uploadState === 'idle' || uploadState === 'error') && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: '1px solid',
              borderColor: semanticColors.border.light,
              borderRadius: 2,
            }}
          >
            {/* Detected flow — read-only badge */}
            {detectedFlow && (
              <Box
                sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 2.5,
                  p: 1.5, borderRadius: 1.5,
                  bgcolor: alpha(semanticColors.text.primary, 0.04),
                  border: '1px solid', borderColor: semanticColors.border.light,
                }}
              >
                <Box sx={{ mt: 0.25 }}>
                  <Chip
                    label="Detected"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      bgcolor: semanticColors.text.primary,
                      color: semanticColors.text.inverse,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: semanticColors.text.primary }}>
                    {detectedFlow.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: semanticColors.text.tertiary, mt: 0.25, lineHeight: 1.45 }}>
                    {detectedFlow.description}
                  </Typography>
                </Box>
              </Box>
            )}

            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: semanticColors.text.tertiary, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
              BOM Details
            </Typography>

            <Stack spacing={2}>
              {importMode === 'package' && (
                <TextField
                  select
                  label="Ingestion profile"
                  value={profileKey}
                  onChange={(e) => setProfileKey(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  helperText={
                    profiles.length === 0
                      ? 'No ingestion profiles are installed on this environment.'
                      : "Named profile that maps this customer's package format (columns, folders, statuses)"
                  }
                >
                  {profiles.map((p) => (
                    <MenuItem key={p.profileKey} value={p.profileKey}>
                      {p.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                label="BOM Name"
                value={bomName}
                onChange={(e) => setBomName(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Landing Gear Assembly Rev C"
                size="small"
                error={!!bomName && bomName.trim().length < 3}
                helperText={
                  !bomName.trim()
                    ? 'Required — at least 3 characters'
                    : bomName.trim().length < 3
                      ? 'Name is too short'
                      : ' '
                }
                inputProps={{ maxLength: 120 }}
              />

              <TextField
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Brief description…"
                size="small"
                inputProps={{ maxLength: 500 }}
                helperText={description ? `${description.length}/500` : ' '}
              />
            </Stack>
          </Paper>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={resetState}
              sx={{
                borderColor: semanticColors.border.default,
                color: semanticColors.text.tertiary,
                '&:hover': { borderColor: semanticColors.border.emphasis, bgcolor: semanticColors.bg.subtle },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!canUpload}
              startIcon={<UploadIcon />}
              sx={{
                bgcolor: semanticColors.text.primary,
                color: semanticColors.text.inverse,
                '&:hover': { bgcolor: '#1E293B' },
                '&:disabled': { bgcolor: semanticColors.bg.muted, color: semanticColors.text.disabled },
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Upload & Analyze
            </Button>
          </Box>
        </motion.div>
      )}
    </Box>
  );
};

export default BomUploadPage;
